import { createElement, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { post, put } from "../../../common/api/apiShared.ts";
import { InputFieldTypeEnum } from "../InputFields.types.ts";
import { InputFieldAddEditDialogProps } from "./InputFieldAddEditDialog.tsx";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { isNullOrWhitespace } from "../../../common/stringOperations.ts";
import { InputFieldSaveModel, InputFieldViewModel } from "./InputFieldAddEditDialog.types.ts";
import { Guid } from "../../../common/guid.ts";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useInputFieldAddEditDialog(props: InputFieldAddEditDialogProps) {
    const snackbar = useDotLegalSnackbar();

    const { translateString } = useTranslation();

    const [saveModel, setSaveModel] = useState<InputFieldSaveModel>({ name: "", type: InputFieldTypeEnum.TextField });
    const [showError, setShowError] = useState(false);
    const [lastAddedOptionId, setLastAddedOptionId] = useState<string | null>(null);

    const url = `/settings/input-fields/${props.inputFieldIdToEdit}`;
    const { isLoading, data } = useDotLegalQuery<InputFieldViewModel>(url, {
        enabled: !isNullOrWhitespace(props.inputFieldIdToEdit),
    });
    useEffect(() => {
        if (data) {
            setSaveModel({ name: data.name, description: data.description, type: data.type, selectOptions: data.list });
        }
    }, [data]);

    const addInputFieldMutation = useDotLegalMutation(addInputFieldApi);
    const onAddInputField = () => {
        addInputFieldMutation.mutate(saveModel!, {
            onSuccess: async (result) => {
                if (result.success()) {
                    props.onSaved();
                    snackbar.show(createElement(Trans, { i18nKey: "itemCreatedSuccess", values: { name: saveModel!.name } }));
                }
            },
        });
    };

    const editInputFieldMutation = useDotLegalMutation(editInputField);
    const onEditInputField = () => {
        editInputFieldMutation.mutate(saveModel!, {
            onSuccess: async (result) => {
                if (result.success()) {
                    props.onSaved();
                    snackbar.show(createElement(Trans, { i18nKey: "itemEditedSuccess", values: { name: saveModel!.name } }));
                }
            },
        });
    };

    const onSaveInputField = () => {
        if (isSaveModelInvalid()) {
            setShowError(true);
            return;
        }

        if (isNullOrWhitespace(props.inputFieldIdToEdit)) {
            onAddInputField();
        } else {
            onEditInputField();
        }
    };

    const modelUpdater = {
        onNameChange: (name: string) => {
            let tempModel = { ...saveModel };
            tempModel.name = name;
            setSaveModel(tempModel);
        },
        onFieldTypeChange: (type: string) => {
            let tempModel = { ...saveModel };
            let newType = Number(type);

            const isNewTypeDropdown = newType === InputFieldTypeEnum.MultiDropDown || newType === InputFieldTypeEnum.SingleDropDown;
            const isOldTypeDropdown = tempModel.type === InputFieldTypeEnum.MultiDropDown || tempModel.type === InputFieldTypeEnum.SingleDropDown;

            if (newType !== tempModel.type) {
                // If newType is dropdown but old type was not dropdown, clear options and add single option
                if (isNewTypeDropdown && !isOldTypeDropdown) {
                    const newId = Guid.newGuid();
                    setLastAddedOptionId(newId);
                    tempModel.selectOptions = [{ id: newId, name: translateString("option") + " 1" }];
                }
                // If newType is not dropdown but old type was dropdown, clear options
                else if (!isNewTypeDropdown && isOldTypeDropdown) {
                    tempModel.selectOptions = undefined;
                }
                // If both are dropdowns, keep existing options (do nothing)
                // If neither are dropdowns, clear options
                else if (!isNewTypeDropdown && !isOldTypeDropdown) {
                    tempModel.selectOptions = undefined;
                }
            }

            tempModel.type = newType;
            setSaveModel(tempModel);
        },
        addSelectOption() {
            let tempModel = { ...saveModel };
            let tempGuid = Guid.newGuid().toString();
            tempModel.selectOptions = [
                ...(tempModel.selectOptions ?? []),
                { id: tempGuid, name: `${translateString("option")} ${(tempModel.selectOptions?.length ?? 0) + 1}` },
            ];
            setSaveModel(tempModel);
            setLastAddedOptionId(tempGuid);
        },
        onSelectOptionChange(id: string, name: string) {
            let tempModel = { ...saveModel };
            tempModel.selectOptions = tempModel.selectOptions?.map((option) => (option.id === id ? { ...option, name } : option)) ?? [];
            setSaveModel(tempModel);
        },
        onRemoveSelectOption(id: string) {
            let tempModel = { ...saveModel };
            tempModel.selectOptions = tempModel.selectOptions?.filter((x) => x.id !== id);
            setSaveModel(tempModel);
        },
        onDescriptionChange(description: string) {
            let tempModel = { ...saveModel };
            tempModel.description = description;
            setSaveModel(tempModel);
        },
    };

    return {
        isLoading,
        lastAddedOptionId,
        saveModel,
        modelUpdater,
        showError,
        inProgress: addInputFieldMutation.isLoading || editInputFieldMutation.isLoading,
        onSaveInputField,
    };

    function isSaveModelInvalid() {
        if (isNullOrWhitespace(saveModel?.name)) {
            return true;
        }

        if (
            (saveModel.type === InputFieldTypeEnum.MultiDropDown || saveModel.type === InputFieldTypeEnum.SingleDropDown) &&
            (saveModel.selectOptions?.length === 0 || saveModel.selectOptions?.some((x) => isNullOrWhitespace(x.name)))
        ) {
            return true;
        }

        return false;
    }

    async function addInputFieldApi(data: InputFieldSaveModel) {
        return await post(`/settings/input-fields?templateEntityType=${props.templateEntityType}`, data);
    }

    async function editInputField(data: InputFieldSaveModel) {
        return await put(`/settings/input-fields/${props.inputFieldIdToEdit}`, data);
    }
}
